import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './AIToolsPage.css';

const AIToolsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('trip-planner');
  const [loading, setLoading] = useState(false);
  const [usageStats, setUsageStats] = useState(null);

  // Trip Planner State
  const [tripPlannerData, setTripPlannerData] = useState({
    destination: '',
    duration: '',
    travelers: { adults: 2, children: [] },
    budget: '',
    interests: [],
    dates: '',
    special_requests: ''
  });
  const [tripPlannerResult, setTripPlannerResult] = useState(null);

  // Marketing Content State
  const [marketingData, setMarketingData] = useState({
    content_type: 'social_media',
    destination: '',
    tone: 'professional',
    platform: 'instagram',
    target_audience: 'general',
    key_points: [],
    call_to_action: ''
  });
  const [marketingResult, setMarketingResult] = useState(null);

  // Interest options for trip planner
  const interestOptions = [
    'Theme Parks', 'Beaches', 'Museums', 'Adventure Sports', 'Fine Dining',
    'Shopping', 'Nightlife', 'Cultural Sites', 'Nature/Wildlife', 'Family Fun',
    'Romantic', 'Photography', 'Food Tours', 'Historical Sites', 'Festivals'
  ];

  useEffect(() => {
    fetchUsageStats();
  }, []);

  const fetchUsageStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ai-tools/usage-stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };

  const handleTripPlannerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTripPlannerResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ai-tools/trip-planner', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tripPlannerData)
      });

      const data = await response.json();

      if (response.ok) {
        setTripPlannerResult(data.data);
        fetchUsageStats(); // Refresh stats
      } else {
        alert(data.error || 'Failed to generate trip itinerary');
      }
    } catch (error) {
      console.error('Error generating trip itinerary:', error);
      alert('Failed to generate trip itinerary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarketingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMarketingResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ai-tools/marketing-content', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(marketingData)
      });

      const data = await response.json();

      if (response.ok) {
        setMarketingResult(data.data);
        fetchUsageStats(); // Refresh stats
      } else {
        alert(data.error || 'Failed to generate marketing content');
      }
    } catch (error) {
      console.error('Error generating marketing content:', error);
      alert('Failed to generate marketing content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInterestToggle = (interest) => {
    const currentInterests = tripPlannerData.interests;
    const newInterests = currentInterests.includes(interest)
      ? currentInterests.filter(i => i !== interest)
      : [...currentInterests, interest];
    
    setTripPlannerData({ ...tripPlannerData, interests: newInterests });
  };

  const addChild = () => {
    const age = prompt('Enter child age:');
    if (age && !isNaN(age) && age > 0 && age < 18) {
      setTripPlannerData({
        ...tripPlannerData,
        travelers: {
          ...tripPlannerData.travelers,
          children: [...tripPlannerData.travelers.children, parseInt(age)]
        }
      });
    }
  };

  const removeChild = (index) => {
    const newChildren = tripPlannerData.travelers.children.filter((_, i) => i !== index);
    setTripPlannerData({
      ...tripPlannerData,
      travelers: { ...tripPlannerData.travelers, children: newChildren }
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Content copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  return (
    <div className="ai-tools-page">
      <div className="ai-tools-header">
        <h1>🤖 AI Travel Assistant</h1>
        <p>Powerful AI tools to help you create amazing travel experiences for your clients</p>
        
        {usageStats && (
          <div className="usage-stats">
            <div className="stat-item">
              <span className="stat-number">{usageStats.totalStats.total_requests ?? 0}</span>
              <span className="stat-label">Total Requests</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{usageStats.totalStats.total_tokens ?? 0}</span>
              <span className="stat-label">Tokens Used</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{Math.round(usageStats.totalStats.avg_response_time || 0)}ms</span>
              <span className="stat-label">Avg Response Time</span>
            </div>
          </div>
        )}
      </div>

      <div className="ai-tools-tabs">
        <button 
          className={`tab-button ${activeTab === 'trip-planner' ? 'active' : ''}`}
          onClick={() => setActiveTab('trip-planner')}
        >
          ✈️ Trip Planner
        </button>
        <button 
          className={`tab-button ${activeTab === 'marketing' ? 'active' : ''}`}
          onClick={() => setActiveTab('marketing')}
        >
          📱 Marketing Content
        </button>
      </div>

      <div className="ai-tools-content">
        {activeTab === 'trip-planner' && (
          <div className="tool-section">
            <div className="tool-form">
              <h2>AI Trip Planner</h2>
              <p>Generate detailed itineraries for your clients using AI</p>
              
              <form onSubmit={handleTripPlannerSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Destination *</label>
                    <input
                      type="text"
                      value={tripPlannerData.destination}
                      onChange={(e) => setTripPlannerData({...tripPlannerData, destination: e.target.value})}
                      placeholder="e.g., Orlando, Florida"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Duration (days) *</label>
                    <input
                      type="number"
                      value={tripPlannerData.duration}
                      onChange={(e) => setTripPlannerData({...tripPlannerData, duration: e.target.value})}
                      placeholder="7"
                      min="1"
                      max="30"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Adults *</label>
                    <input
                      type="number"
                      value={tripPlannerData.travelers.adults}
                      onChange={(e) => setTripPlannerData({
                        ...tripPlannerData, 
                        travelers: {...tripPlannerData.travelers, adults: parseInt(e.target.value) || 0}
                      })}
                      min="1"
                      max="20"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Children</label>
                    <div className="children-section">
                      {tripPlannerData.travelers.children.map((age, index) => (
                        <span key={index} className="child-tag">
                          Age {age} 
                          <button type="button" onClick={() => removeChild(index)}>×</button>
                        </span>
                      ))}
                      <button type="button" onClick={addChild} className="add-child-btn">+ Add Child</button>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Budget</label>
                  <input
                    type="text"
                    value={tripPlannerData.budget}
                    onChange={(e) => setTripPlannerData({...tripPlannerData, budget: e.target.value})}
                    placeholder="e.g., $5,000 total or $500/day"
                  />
                </div>

                <div className="form-group">
                  <label>Travel Dates</label>
                  <input
                    type="text"
                    value={tripPlannerData.dates}
                    onChange={(e) => setTripPlannerData({...tripPlannerData, dates: e.target.value})}
                    placeholder="e.g., March 15-22, 2024 or Flexible"
                  />
                </div>

                <div className="form-group">
                  <label>Interests</label>
                  <div className="interests-grid">
                    {interestOptions.map(interest => (
                      <button
                        key={interest}
                        type="button"
                        className={`interest-tag ${tripPlannerData.interests.includes(interest) ? 'selected' : ''}`}
                        onClick={() => handleInterestToggle(interest)}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Special Requests</label>
                  <textarea
                    value={tripPlannerData.special_requests}
                    onChange={(e) => setTripPlannerData({...tripPlannerData, special_requests: e.target.value})}
                    placeholder="Any special requirements, accessibility needs, dietary restrictions, etc."
                    rows="3"
                  />
                </div>

                <button type="submit" className="generate-btn" disabled={loading}>
                  {loading ? '🤖 Generating...' : '✨ Generate Itinerary'}
                </button>
              </form>
            </div>

            {tripPlannerResult && (
              <div className="tool-result">
                <div className="result-header">
                  <h3>Generated Itinerary</h3>
                  <div className="result-actions">
                    <button onClick={() => copyToClipboard(tripPlannerResult.itinerary)}>
                      📋 Copy
                    </button>
                    <span className="result-stats">
                      {tripPlannerResult.tokensUsed} tokens • {tripPlannerResult.responseTime}ms
                    </span>
                  </div>
                </div>
                <div className="result-content">
                  <pre>{tripPlannerResult.itinerary}</pre>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'marketing' && (
          <div className="tool-section">
            <div className="tool-form">
              <h2>Marketing Content Generator</h2>
              <p>Create engaging marketing content for social media, emails, and more</p>
              
              <form onSubmit={handleMarketingSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Content Type *</label>
                    <select
                      value={marketingData.content_type}
                      onChange={(e) => setMarketingData({...marketingData, content_type: e.target.value})}
                      required
                    >
                      <option value="social_media">Social Media Post</option>
                      <option value="email">Email Campaign</option>
                      <option value="blog">Blog Article</option>
                      <option value="proposal">Client Proposal</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Platform</label>
                    <select
                      value={marketingData.platform}
                      onChange={(e) => setMarketingData({...marketingData, platform: e.target.value})}
                    >
                      <option value="instagram">Instagram</option>
                      <option value="facebook">Facebook</option>
                      <option value="email">Email</option>
                      <option value="blog">Blog</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Destination *</label>
                    <input
                      type="text"
                      value={marketingData.destination}
                      onChange={(e) => setMarketingData({...marketingData, destination: e.target.value})}
                      placeholder="e.g., Santorini, Greece"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Tone</label>
                    <select
                      value={marketingData.tone}
                      onChange={(e) => setMarketingData({...marketingData, tone: e.target.value})}
                    >
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="exciting">Exciting</option>
                      <option value="romantic">Romantic</option>
                      <option value="luxury">Luxury</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Target Audience</label>
                  <input
                    type="text"
                    value={marketingData.target_audience}
                    onChange={(e) => setMarketingData({...marketingData, target_audience: e.target.value})}
                    placeholder="e.g., couples, families, adventure travelers"
                  />
                </div>

                <div className="form-group">
                  <label>Call to Action</label>
                  <input
                    type="text"
                    value={marketingData.call_to_action}
                    onChange={(e) => setMarketingData({...marketingData, call_to_action: e.target.value})}
                    placeholder="e.g., Book now for early bird pricing!"
                  />
                </div>

                <button type="submit" className="generate-btn" disabled={loading}>
                  {loading ? '🤖 Generating...' : '✨ Generate Content'}
                </button>
              </form>
            </div>

            {marketingResult && (
              <div className="tool-result">
                <div className="result-header">
                  <h3>Generated Content</h3>
                  <div className="result-actions">
                    <button onClick={() => copyToClipboard(marketingResult.content)}>
                      📋 Copy
                    </button>
                    <span className="result-stats">
                      {marketingResult.tokensUsed} tokens • {marketingResult.responseTime}ms
                    </span>
                  </div>
                </div>
                <div className="result-content">
                  <pre>{marketingResult.content}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIToolsPage;
